import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

export interface DynamicCardProps {
  title?: string;
  subtitle?: string;
  description?: string;
  image?: {
    src: string;
    alt?: string;
    height?: number | string;
    aspectRatio?: number;
  };
  avatar?: {
    src?: string;
    alt?: string;
    initials?: string;
    size?: number;
  };
  
  primaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'text' | 'outlined' | 'contained';
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    size?: 'small' | 'medium' | 'large';
    disabled?: boolean;
    loading?: boolean;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'text' | 'outlined' | 'contained';
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    size?: 'small' | 'medium' | 'large';
    disabled?: boolean;
  };
  
  icon?: {
    component: React.ComponentType<any>;
    color?: string;
    size?: number;
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    onClick?: () => void;
  };
  
  badges?: Array<{
    label: string;
    color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    variant?: 'filled' | 'outlined';
    size?: 'small' | 'medium';
  }>;
  
  metadata?: Array<{
    label: string;
    value: string | number;
    icon?: React.ComponentType<any>;
  }>;
  
  width?: number | string;
  height?: number | string;
  maxWidth?: number | string;
  minHeight?: number | string;
  
  elevation?: number;
  variant?: 'elevation' | 'outlined';
  borderRadius?: number | string;
  backgroundColor?: string;
  
  clickable?: boolean;
  onClick?: () => void;
  hoverEffect?: boolean;
  loading?: boolean;
  

  transition?: {
    duration?: number;
    easing?: string;
    properties?: string[];
  };
  
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  mediaSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  
  children?: React.ReactNode;
}

const DynamicCard: React.FC<DynamicCardProps> = ({
  title,
  subtitle,
  description,
  image,
  avatar,
  primaryAction,
  secondaryAction,
  icon,
  badges,
  metadata,
  width = 320,
  height,
  maxWidth,
  minHeight,
  elevation = 1,
  variant = 'elevation',
  borderRadius = 2,
  backgroundColor,
  clickable = false,
  onClick,
  hoverEffect = false,
  loading = false,
  transition = { duration: 0.2, easing: 'ease-in-out' },
  sx,
  contentSx,
  mediaSx,
  actionsSx,
  children,
}) => {
  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };


  const cardStyles: SxProps<Theme> = {
    width,
    height,
    maxWidth,
    minHeight,
    borderRadius,
    backgroundColor,
    cursor: clickable ? 'pointer' : 'default',
    transition: transition.properties 
      ? transition.properties.map(prop => `${prop} ${transition.duration}s ${transition.easing}`).join(', ')
      : `all ${transition.duration}s ${transition.easing}`,
    ...(hoverEffect && {
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: (theme) => theme.shadows[8],
      },
    }),
    ...sx,
  };

  const contentStyles: SxProps<Theme> = {
    p: 2,
    ...contentSx,
  };

  const mediaStyles: SxProps<Theme> = {
    height: image?.height || 200,
    ...(image?.aspectRatio && {
      aspectRatio: image.aspectRatio,
    }),
    ...mediaSx,
  };

  if (loading) {
    return (
      <Card
        variant={variant}
        elevation={elevation}
        sx={cardStyles}
      >
        <CardContent sx={contentStyles}>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
          <Skeleton variant="text" width="80%" height={20} sx={{ mt: 2 }} />
          <Skeleton variant="text" width="60%" height={20} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      variant={variant}
      elevation={elevation}
      sx={cardStyles}
      onClick={handleClick}
    >
      

      {image && (
        <CardMedia
          component="img"
          image={image.src}
          alt={image.alt || title || 'Card image'}
          sx={mediaStyles}
        />
      )}

      <CardContent sx={{...contentStyles}}>
        {(avatar || title || subtitle) && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            {avatar && (
              <Avatar
                src={avatar.src}
                alt={avatar.alt}
                sx={{
                  width: avatar.size || 40,
                  height: avatar.size || 40,
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                {avatar.initials}
              </Avatar>
            )}
            
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, minWidth: 0, }}>
                {title && (
                  <Tooltip title={title} >
                    <Typography variant='h6'>
                    {title}
                    </Typography>
                  </Tooltip>
                )}
                {subtitle && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
              
              {icon && !icon.position && (
                <Box sx={{ ml: 1, flexShrink: 0 }}>
                  <icon.component
                    sx={{
                      fontSize: 20,
                      color: icon.color || 'inherit',
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}

        {badges && badges.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {badges.map((badge, index) => (
              <Chip
                key={index}
                label={badge.label}
                color={badge.color}
                variant={badge.variant}
                size={badge.size}
              />
            ))}
          </Box>
        )}

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}

        {metadata && metadata.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {metadata.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                {item.icon && (
                  <item.icon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  {item.label}:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {children}

        {(primaryAction || secondaryAction) && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 2,
            ...actionsSx 
          }}>
            <Box>
              {secondaryAction && (
                <Button
                  variant={secondaryAction.variant || 'text'}
                  color={secondaryAction.color || 'primary'}
                  size={secondaryAction.size || 'small'}
                  disabled={secondaryAction.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    secondaryAction.onClick();
                  }}
                >
                  {secondaryAction.label}
                </Button>
              )}
            </Box>
            
            {primaryAction && (
              <Button
                variant={primaryAction.variant || 'contained'}
                color={primaryAction.color || 'primary'}
                size={primaryAction.size || 'medium'}
                disabled={primaryAction.disabled || primaryAction.loading}
                onClick={(e) => {
                  e.stopPropagation();
                  primaryAction.onClick();
                }}
              >
                {primaryAction.loading ? 'Loading...' : primaryAction.label}
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DynamicCard; 